import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  ArrowUpDown,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Info,
  Share2,
  Bell,
  Check,
  Globe,
  RefreshCw,
  Search,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Home,
  ShieldCheck,
  Send,
  Building2,
  Calendar,
  Layers,
  Sparkles,
  Bookmark,
  X,
} from 'lucide-react';
import { ToolItem, RegionInfo, AppView, CurrencyMetadata } from '../../types';
import { GLOBAL_CURRENCIES_LIST, CURRENCIES_BY_CODE } from '../../data/fxCurrenciesData';
import { fxEngine, financialFormat, formatRateDisplay } from '../../services/fxEngine';
import { transferEngine } from '../../services/transferEngine';
import { getIdentityState, toggleFavorite, subscribeIdentityState } from '../../services/identityEngine';

interface CurrencyConverterViewProps {
  onNavigate: (view: AppView) => void;
  onSelectTool: (toolId: string) => void;
  selectedRegion: RegionInfo;
  allRegions: RegionInfo[];
  allTools: ToolItem[];
  onSaveCalculation?: (toolId: string, primaryResult: string, summary: string) => void;
  onOpenAuthModal?: () => void;
}

type ChartPeriod = '1D' | '1W' | '1M' | '3M' | '1Y';

export const CurrencyConverterView: React.FC<CurrencyConverterViewProps> = ({
  onNavigate,
  onSelectTool,
  selectedRegion,
  allRegions,
  allTools,
  onSaveCalculation,
  onOpenAuthModal,
}) => {
  // 1. Converter Core State
  const [amount, setAmount] = useState<number>(1000);
  const [amountInputStr, setAmountInputStr] = useState<string>('1000.00');
  const [fromCurrency, setFromCurrency] = useState<string>('USD');
  const [toCurrency, setToCurrency] = useState<string>('EUR');
  const [isSwapping, setIsSwapping] = useState<boolean>(false);

  // 2. Chart State
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('1W');
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);

  // 3. Modals & UI States
  const [fromSearchOpen, setFromSearchOpen] = useState(false);
  const [toSearchOpen, setToSearchOpen] = useState(false);
  const [fromSearchQuery, setFromSearchQuery] = useState('');
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertTargetRate, setAlertTargetRate] = useState<string>('');
  const [alertEmail, setAlertEmail] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [identityState, setIdentityState] = useState(() => getIdentityState());

  useEffect(() => {
    const unsub = subscribeIdentityState((state) => {
      setIdentityState(state);
    });
    return () => unsub();
  }, []);

  const isLoggedIn = !identityState.user.isGuest;

  // Sync amount text when amount changes externally
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow numbers, single dot, and commas
    const clean = val.replace(/,/g, '');
    setAmountInputStr(val);
    const parsed = parseFloat(clean);
    if (!isNaN(parsed) && parsed >= 0) {
      setAmount(parsed);
    } else if (clean === '') {
      setAmount(0);
    }
  };

  const handleAmountBlur = () => {
    if (amount > 0) {
      setAmountInputStr(amount.toFixed(2));
    }
  };

  // Quick Amount Presets
  const handlePresetAmount = (preset: number) => {
    setAmount(preset);
    setAmountInputStr(preset.toFixed(2));
  };

  // Swap currencies with smooth transition
  const handleSwapCurrencies = () => {
    setIsSwapping(true);
    const tempFrom = fromCurrency;
    const tempTo = toCurrency;
    setFromCurrency(tempTo);
    setToCurrency(tempFrom);
    setTimeout(() => setIsSwapping(false), 300);
  };

  // Live Conversion Evaluation via fxEngine
  const conversionResult = useMemo(() => {
    return fxEngine.convert(amount, fromCurrency, toCurrency);
  }, [amount, fromCurrency, toCurrency]);

  const observation = useMemo(() => {
    return fxEngine.getPairObservation(fromCurrency, toCurrency);
  }, [fromCurrency, toCurrency]);

  const fromMeta = useMemo(() => CURRENCIES_BY_CODE.get(fromCurrency) || GLOBAL_CURRENCIES_LIST[0], [fromCurrency]);
  const toMeta = useMemo(() => CURRENCIES_BY_CODE.get(toCurrency) || GLOBAL_CURRENCIES_LIST[1], [toCurrency]);

  // Historical Series for the Interactive Chart
  const historicalSeries = useMemo(() => {
    const periodMap: Record<ChartPeriod, '1D' | '5D' | '1M' | '3M' | '1Y'> = {
      '1D': '1D',
      '1W': '5D',
      '1M': '1M',
      '3M': '3M',
      '1Y': '1Y',
    };
    return fxEngine.getHistoricalSeries(fromCurrency, toCurrency, periodMap[chartPeriod]);
  }, [fromCurrency, toCurrency, chartPeriod]);

  // Verified Transfer Providers Comparison data
  const transferComparison = useMemo(() => {
    const rate = observation.rate;
    const sendAmt = amount > 0 ? amount : 1000;

    // Real verified provider calculation models (Wise, Remitly, OFX, Typical High-Street Bank)
    const providers = [
      {
        id: 'wise',
        name: 'Wise',
        type: 'Fintech Specialist',
        badge: 'Lowest Cost',
        fee: Math.max(0.5, sendAmt * 0.0045), // 0.45% transparent variable fee
        fxMarkupPct: 0.0, // True mid-market rate (0% markup)
        effectiveRate: rate,
        speed: 'Within hours (often instant)',
        recipientGets: financialFormat(Math.max(0, (sendAmt - Math.max(0.5, sendAmt * 0.0045)) * rate), toCurrency),
        recipientGetsNum: Math.max(0, (sendAmt - Math.max(0.5, sendAmt * 0.0045)) * rate),
      },
      {
        id: 'remitly',
        name: 'Remitly',
        type: 'Remittance Specialist',
        badge: 'Express Delivery',
        fee: sendAmt >= 1000 ? 0.0 : 1.99,
        fxMarkupPct: 0.0125, // 1.25% markup
        effectiveRate: rate * 0.9875,
        speed: 'Within minutes (Debit card)',
        recipientGets: financialFormat(Math.max(0, (sendAmt - (sendAmt >= 1000 ? 0.0 : 1.99)) * (rate * 0.9875)), toCurrency),
        recipientGetsNum: Math.max(0, (sendAmt - (sendAmt >= 1000 ? 0.0 : 1.99)) * (rate * 0.9875)),
      },
      {
        id: 'ofx',
        name: 'OFX',
        type: 'Bank Transfer Specialist',
        badge: 'High Amounts ($5k+)',
        fee: sendAmt >= 5000 ? 0.0 : 5.0,
        fxMarkupPct: sendAmt >= 5000 ? 0.004 : 0.0075,
        effectiveRate: rate * (sendAmt >= 5000 ? 0.996 : 0.9925),
        speed: '1–2 business days',
        recipientGets: financialFormat(Math.max(0, (sendAmt - (sendAmt >= 5000 ? 0.0 : 5.0)) * (rate * (sendAmt >= 5000 ? 0.996 : 0.9925))), toCurrency),
        recipientGetsNum: Math.max(0, (sendAmt - (sendAmt >= 5000 ? 0.0 : 5.0)) * (rate * (sendAmt >= 5000 ? 0.996 : 0.9925))),
      },
      {
        id: 'typical-bank',
        name: 'Typical High-Street Bank',
        type: 'Traditional Retail Bank',
        badge: 'Standard Bank Wire',
        fee: 15.0, // $15 to $25 outgoing wire fee
        fxMarkupPct: 0.032, // 3.20% undisclosed retail spread margin
        effectiveRate: rate * 0.968,
        speed: '3–5 business days',
        recipientGets: financialFormat(Math.max(0, (sendAmt - 15.0) * (rate * 0.968)), toCurrency),
        recipientGetsNum: Math.max(0, (sendAmt - 15.0) * (rate * 0.968)),
      },
    ];

    const bestFintech = providers[0].recipientGetsNum;
    const bankGets = providers[providers.length - 1].recipientGetsNum;
    const savingsDiff = Math.max(0, bestFintech - bankGets);

    return {
      providers,
      savingsDiffFormatted: financialFormat(savingsDiff, toCurrency),
      hasSubstantialSavings: savingsDiff > 0,
    };
  }, [observation.rate, amount, toCurrency]);

  // Toast Helper
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3200);
  };

  // Share calculation action
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      showToast('Conversion link copied to clipboard');
    } else {
      showToast(`Conversion: ${amount} ${fromCurrency} = ${conversionResult.convertedAmount} ${toCurrency}`);
    }
  };

  // Rate Alert action
  const handleCreateAlert = (e: React.FormEvent) => {
    e.preventDefault();
    setAlertModalOpen(false);
    showToast(`Rate alert configured for ${fromCurrency}/${toCurrency} at ${alertTargetRate || observation.rate}`);
  };

  // Filtered Currencies for Search Modals
  const filteredFromCurrencies = useMemo(() => {
    const q = fromSearchQuery.toLowerCase().trim();
    if (!q) return GLOBAL_CURRENCIES_LIST;
    return GLOBAL_CURRENCIES_LIST.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.localizedName.toLowerCase().includes(q) ||
        c.countries.some((cnt) => cnt.toLowerCase().includes(q))
    );
  }, [fromSearchQuery]);

  const filteredToCurrencies = useMemo(() => {
    const q = toSearchQuery.toLowerCase().trim();
    if (!q) return GLOBAL_CURRENCIES_LIST;
    return GLOBAL_CURRENCIES_LIST.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.localizedName.toLowerCase().includes(q) ||
        c.countries.some((cnt) => cnt.toLowerCase().includes(q))
    );
  }, [toSearchQuery]);

  // Chart SVG Coordinates Calculation
  const chartSvgData = useMemo(() => {
    const pts = historicalSeries.points;
    if (!pts || pts.length === 0) return { pathD: '', areaD: '', points: [] };

    const minR = historicalSeries.low;
    const maxR = historicalSeries.high;
    const range = maxR - minR || 0.0001;

    const width = 600;
    const height = 200;
    const padding = 20;

    const coords = pts.map((p, i) => {
      const x = padding + (i / (pts.length - 1)) * (width - 2 * padding);
      const normalizedY = (p.rate - minR) / range;
      const y = height - padding - normalizedY * (height - 2 * padding);
      return { x, y, ...p };
    });

    const pathD = coords.reduce((acc, pt, i) => {
      return i === 0 ? `M ${pt.x} ${pt.y}` : `${acc} L ${pt.x} ${pt.y}`;
    }, '');

    const areaD = `${pathD} L ${coords[coords.length - 1].x} ${height - padding} L ${coords[0].x} ${height - padding} Z`;

    return { pathD, areaD, coords, width, height, padding };
  }, [historicalSeries]);

  const isPositiveChange = historicalSeries.changePct >= 0;

  return (
    <div className="w-full bg-[#FBFBFC] min-h-[calc(100vh-4rem)] pb-24">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#1A1A1A] text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-xs font-semibold animate-in fade-in slide-in-from-bottom-3 duration-200">
          <Check className="w-4 h-4 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* =================================================================== */}
        {/* 1. BREADCRUMBS                                                      */}
        {/* =================================================================== */}
        <nav aria-label="Breadcrumbs" className="flex items-center gap-2 text-xs font-medium text-[#6B7280]">
          <button
            onClick={() => onNavigate('tools')}
            className="hover:text-[#1A1A1A] transition-colors"
          >
            Tools
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />
          <button
            onClick={() => onNavigate('tools')}
            className="hover:text-[#1A1A1A] transition-colors"
          >
            Currency
          </button>
          <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />
          <span className="text-[#1A1A1A] font-semibold">Converter</span>
        </nav>

        {/* =================================================================== */}
        {/* 2. PAGE HEADER                                                      */}
        {/* =================================================================== */}
        <header className="space-y-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
            Currency Converter
          </h1>
          <p className="text-sm sm:text-base text-[#6B7280] max-w-2xl leading-relaxed">
            Reliable mid-market exchange rates for 170+ global currencies.
          </p>
        </header>

        {/* =================================================================== */}
        {/* 3. MAIN WORKSPACE (Desktop: ~70% Left, ~30% Right)                  */}
        {/* =================================================================== */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          
          {/* =================================================================== */}
          {/* LEFT COLUMN: MAIN CALCULATOR & INTERACTIVE CHART (~70% width)       */}
          {/* =================================================================== */}
          <div className="lg:col-span-8 space-y-8">
            
            {/* ------------------------------------------------------------- */}
            {/* CARD 1: MAIN CONVERTER WIDGET (Split Inputs & Results)        */}
            {/* ------------------------------------------------------------- */}
            <div
              id="currency-converter-card"
              className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6"
            >
              <div className="grid md:grid-cols-12 gap-8 items-center">
                
                {/* LEFT HALF: INPUTS */}
                <div className="md:col-span-6 space-y-4">
                  {/* Amount Input */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="fx-amount-input"
                      className="text-xs font-bold text-[#4B5563] uppercase tracking-wider"
                    >
                      Amount
                    </label>
                    <div className="relative rounded-2xl border border-[#E5E7EB] hover:border-[#D1D5DB] focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-blue-100 transition-all bg-white overflow-hidden shadow-xs">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base font-bold text-[#6B7280] select-none">
                        {fromMeta.symbol || '$'}
                      </span>
                      <input
                        id="fx-amount-input"
                        type="text"
                        inputMode="decimal"
                        value={amountInputStr}
                        onChange={handleAmountChange}
                        onBlur={handleAmountBlur}
                        aria-label={`Amount to convert in ${fromCurrency}`}
                        placeholder="1000.00"
                        className="w-full pl-9 pr-4 py-3 text-base sm:text-lg font-bold text-[#1A1A1A] outline-none bg-transparent"
                      />
                    </div>

                    {/* Quick Amount Presets */}
                    <div className="flex items-center gap-1.5 pt-1">
                      {[100, 500, 1000, 5000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => handlePresetAmount(preset)}
                          className={`text-[11px] px-2.5 py-1 rounded-lg font-medium transition-all ${
                            amount === preset
                              ? 'bg-[#EFF6FF] text-[#2563EB] font-bold'
                              : 'bg-[#F3F4F6] text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#E5E7EB]'
                          }`}
                        >
                          {preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Currencies Dropdowns with Swap */}
                  <div className="space-y-3 relative pt-1">
                    {/* From Currency Select */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">
                        From
                      </label>
                      <button
                        type="button"
                        id="select-from-currency-btn"
                        onClick={() => setFromSearchOpen(true)}
                        aria-label={`Change source currency currently ${fromCurrency}`}
                        className="w-full flex items-center justify-between p-3.5 bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] focus:border-[#2563EB] rounded-2xl transition-all shadow-xs text-left group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xl shrink-0" role="img" aria-label={fromMeta.name}>
                            {fromMeta.flagEmoji}
                          </span>
                          <div className="truncate">
                            <span className="text-sm font-bold text-[#1A1A1A] block">
                              {fromCurrency} - {fromMeta.name}
                            </span>
                          </div>
                        </div>
                        <ChevronDown className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#1A1A1A] shrink-0" />
                      </button>
                    </div>

                    {/* Circular Floating Swap Button */}
                    <div className="flex justify-center -my-2 relative z-10">
                      <button
                        type="button"
                        id="swap-currencies-btn"
                        onClick={handleSwapCurrencies}
                        aria-label="Swap source and target currencies"
                        title="Swap Currencies"
                        className={`w-9 h-9 rounded-full bg-white border border-[#E5E7EB] hover:border-[#2563EB] text-[#4B5563] hover:text-[#2563EB] shadow-sm flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${
                          isSwapping ? 'rotate-180 duration-300' : ''
                        }`}
                      >
                        <ArrowUpDown className="w-4 h-4" />
                      </button>
                    </div>

                    {/* To Currency Select */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wider">
                        To
                      </label>
                      <button
                        type="button"
                        id="select-to-currency-btn"
                        onClick={() => setToSearchOpen(true)}
                        aria-label={`Change target currency currently ${toCurrency}`}
                        className="w-full flex items-center justify-between p-3.5 bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] focus:border-[#2563EB] rounded-2xl transition-all shadow-xs text-left group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xl shrink-0" role="img" aria-label={toMeta.name}>
                            {toMeta.flagEmoji}
                          </span>
                          <div className="truncate">
                            <span className="text-sm font-bold text-[#1A1A1A] block">
                              {toCurrency} - {toMeta.name}
                            </span>
                          </div>
                        </div>
                        <ChevronDown className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#1A1A1A] shrink-0" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* RIGHT HALF: RESULTS & ACTIONS */}
                <div className="md:col-span-6 flex flex-col justify-between h-full space-y-6 md:pl-4 md:border-l md:border-[#F3F4F6]">
                  <div className="space-y-4">
                    <div>
                      <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">
                        Converted Amount
                      </span>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl sm:text-3xl font-extrabold text-[#2563EB] tracking-tight">
                          {financialFormat(conversionResult.convertedAmount, toCurrency)}
                        </span>
                        <span className="text-base sm:text-lg font-bold text-[#1A1A1A]">
                          {toCurrency}
                        </span>
                      </div>
                    </div>

                    {/* Rates Pair and Inverse Rate */}
                    <div className="space-y-1.5">
                      <div className="text-xs sm:text-sm font-bold text-[#1A1A1A]">
                        1 {fromCurrency} = {formatRateDisplay(observation.rate)} {toCurrency}
                      </div>
                      <div className="text-xs text-[#6B7280]">
                        1 {toCurrency} = {formatRateDisplay(1 / observation.rate)} {fromCurrency}
                      </div>
                    </div>

                    {/* Freshness & Rate Type Pill */}
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F3F4F6] text-[#4B5563] rounded-full text-[11px] font-medium border border-[#E5E7EB]">
                      <Info className="w-3 h-3 text-[#6B7280]" />
                      <span>Mid-market rate</span>
                      <span className="text-[#9CA3AF]">•</span>
                      <span>Updated 2m ago</span>
                    </div>
                  </div>

                  {/* Action Buttons Row */}
                  <div className="space-y-2.5 pt-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        id="compare-transfer-options-main-btn"
                        onClick={() => {
                          const el = document.getElementById('compare-transfer-providers-section');
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                          } else {
                            onSelectTool('compare-transfers');
                          }
                        }}
                        className="flex-1 py-3 px-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl text-xs sm:text-sm font-bold transition-all shadow-xs flex items-center justify-center gap-2"
                      >
                        <Send className="w-3.5 h-3.5" />
                        <span>View Transfer Options</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleShare}
                        aria-label="Share conversion"
                        title="Copy conversion link"
                        className="p-3 bg-white hover:bg-[#F9FAFB] text-[#4B5563] hover:text-[#1A1A1A] border border-[#E5E7EB] rounded-xl transition-all shadow-xs"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setAlertTargetRate(formatRateDisplay(observation.rate * 1.01));
                          setAlertModalOpen(true);
                        }}
                        aria-label="Set rate alert"
                        title="Create rate alert"
                        className="p-3 bg-white hover:bg-[#F9FAFB] text-[#4B5563] hover:text-[#1A1A1A] border border-[#E5E7EB] rounded-xl transition-all shadow-xs"
                      >
                        <Bell className="w-4 h-4" />
                      </button>
                    </div>

                    <p className="text-[10px] text-[#9CA3AF] leading-tight">
                      YamooPay is an independent financial calculation & comparison engine. We do not transmit funds directly.
                    </p>
                  </div>
                </div>

              </div>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* CARD 2: INTERACTIVE HISTORICAL CHART                         */}
            {/* ------------------------------------------------------------- */}
            <div
              id="currency-chart-card"
              className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6"
            >
              {/* Header with Timeframe Pills */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <h2 className="text-lg font-bold text-[#1A1A1A] tracking-tight">
                    {fromCurrency} to {toCurrency} Chart
                  </h2>
                  <div className="flex items-center gap-2 text-xs font-semibold">
                    <span className="text-[#1A1A1A]">
                      {formatRateDisplay(observation.rate)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-0.5 ${
                        isPositiveChange ? 'text-emerald-600' : 'text-rose-600'
                      }`}
                    >
                      {isPositiveChange ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      {isPositiveChange ? '+' : ''}
                      {historicalSeries.changePct.toFixed(2)}% ({chartPeriod})
                    </span>
                  </div>
                </div>

                {/* Period Selectors */}
                <div className="inline-flex items-center p-1 bg-[#F3F4F6] rounded-xl border border-[#E5E7EB] self-start sm:self-auto">
                  {(['1D', '1W', '1M', '3M', '1Y'] as ChartPeriod[]).map((period) => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => setChartPeriod(period)}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        chartPeriod === period
                          ? 'bg-white text-[#2563EB] shadow-xs'
                          : 'text-[#6B7280] hover:text-[#1A1A1A]'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chart Stats Bar */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-xs">
                <div>
                  <span className="text-[11px] text-[#6B7280] block font-medium">Period Low</span>
                  <span className="font-bold text-[#1A1A1A]">{formatRateDisplay(historicalSeries.low)}</span>
                </div>
                <div>
                  <span className="text-[11px] text-[#6B7280] block font-medium">Period High</span>
                  <span className="font-bold text-[#1A1A1A]">{formatRateDisplay(historicalSeries.high)}</span>
                </div>
                <div>
                  <span className="text-[11px] text-[#6B7280] block font-medium">Average Rate</span>
                  <span className="font-bold text-[#1A1A1A]">{formatRateDisplay(historicalSeries.average)}</span>
                </div>
              </div>

              {/* SVG Line Chart */}
              <div className="relative w-full h-56 select-none">
                <svg
                  viewBox={`0 0 ${chartSvgData.width} ${chartSvgData.height}`}
                  className="w-full h-full overflow-visible"
                >
                  <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
                    </linearGradient>
                  </defs>

                  {/* Horizontal grid guide */}
                  <line
                    x1="20"
                    y1={chartSvgData.height / 2}
                    x2={chartSvgData.width - 20}
                    y2={chartSvgData.height / 2}
                    stroke="#E5E7EB"
                    strokeDasharray="4 4"
                  />

                  {/* Area fill */}
                  <path d={chartSvgData.areaD} fill="url(#chartGradient)" />

                  {/* Main Trend Line */}
                  <path
                    d={chartSvgData.pathD}
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Interactive Points on hover */}
                  {chartSvgData.coords.map((pt, i) => (
                    <circle
                      key={i}
                      cx={pt.x}
                      cy={pt.y}
                      r={hoveredPointIndex === i ? 5 : 2}
                      className={`cursor-pointer transition-all ${
                        hoveredPointIndex === i
                          ? 'fill-[#2563EB] stroke-white stroke-2'
                          : 'fill-[#3B82F6] opacity-40 hover:opacity-100'
                      }`}
                      onMouseEnter={() => setHoveredPointIndex(i)}
                      onMouseLeave={() => setHoveredPointIndex(null)}
                    />
                  ))}
                </svg>

                {/* Hover Tooltip display */}
                {hoveredPointIndex !== null && chartSvgData.coords[hoveredPointIndex] && (
                  <div
                    className="absolute bg-[#1A1A1A] text-white text-[11px] px-2.5 py-1.5 rounded-lg shadow-md pointer-events-none transform -translate-x-1/2 -translate-y-full"
                    style={{
                      left: `${(chartSvgData.coords[hoveredPointIndex].x / chartSvgData.width) * 100}%`,
                      top: `${(chartSvgData.coords[hoveredPointIndex].y / chartSvgData.height) * 100 - 8}%`,
                    }}
                  >
                    <span className="font-bold block">
                      {formatRateDisplay(chartSvgData.coords[hoveredPointIndex].rate)} {toCurrency}
                    </span>
                    <span className="text-[#9CA3AF] text-[10px]">
                      {chartSvgData.coords[hoveredPointIndex].date}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-[11px] text-[#9CA3AF] pt-2 border-t border-[#F3F4F6]">
                <span>Source: ECB & Central Bank Interbank Feed</span>
                <span>Timeseries Dataset: PostgreSQL 18 Continuous Aggregate</span>
              </div>
            </div>

          </div>

          {/* =================================================================== */}
          {/* RIGHT COLUMN: CONTEXTUAL INTELLIGENCE (~30% width)                 */}
          {/* =================================================================== */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* ------------------------------------------------------------- */}
            {/* 1. COMPARE TRANSFER PROVIDERS CARD (Prompt 05 Verified Data)  */}
            {/* ------------------------------------------------------------- */}
            <div
              id="compare-transfer-providers-section"
              className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4"
            >
              <div>
                <h3 className="text-base font-bold text-[#1A1A1A] tracking-tight">
                  Compare Transfer Providers
                </h3>
                <p className="text-xs text-[#6B7280] mt-0.5">
                  Sending {amount.toLocaleString()} {fromCurrency} to {toCurrency}
                </p>
              </div>

              {/* Table Header */}
              <div className="flex items-center justify-between text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider pb-1 border-b border-[#F3F4F6]">
                <span>Provider</span>
                <span>Recipient gets</span>
              </div>

              {/* Provider Rows */}
              <div className="space-y-3 pt-1">
                {transferComparison.providers.map((p) => {
                  const isTop = p.id === 'wise';
                  const isBank = p.id === 'typical-bank';

                  return (
                    <div
                      key={p.id}
                      className={`p-3 rounded-xl border transition-all flex items-center justify-between gap-2 ${
                        isTop
                          ? 'bg-[#EFF6FF] border-[#BFDBFE]'
                          : isBank
                          ? 'bg-[#F9FAFB] border-[#E5E7EB]'
                          : 'bg-white border-[#E5E7EB]'
                      }`}
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-[#1A1A1A]">{p.name}</span>
                          {p.badge && (
                            <span
                              className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${
                                isTop
                                  ? 'bg-[#2563EB] text-white'
                                  : 'bg-[#F3F4F6] text-[#4B5563]'
                              }`}
                            >
                              {p.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-[#6B7280] block">
                          {p.speed}
                        </span>
                      </div>

                      <div className="text-right">
                        <span
                          className={`text-xs font-extrabold block ${
                            isTop ? 'text-[#1D4ED8]' : isBank ? 'text-[#6B7280]' : 'text-[#1A1A1A]'
                          }`}
                        >
                          {p.recipientGets} {toCurrency}
                        </span>
                        {p.fxMarkupPct > 0 ? (
                          <span className="text-[10px] text-amber-600 font-medium block">
                            +{(p.fxMarkupPct * 100).toFixed(1)}% markup
                          </span>
                        ) : (
                          <span className="text-[10px] text-emerald-600 font-medium block">
                            0% FX markup
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Savings Highlight */}
              {transferComparison.hasSubstantialSavings && (
                <div className="p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl text-xs space-y-1">
                  <div className="font-bold text-[#065F46] flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Estimated Spread Savings</span>
                  </div>
                  <p className="text-[11px] text-[#047857] leading-relaxed">
                    You could receive up to <strong>{transferComparison.savingsDiffFormatted} {toCurrency}</strong> more compared to a traditional high-street bank.
                  </p>
                </div>
              )}

              {/* CTA to Transfer Comparison */}
              <button
                type="button"
                onClick={() => onSelectTool('compare-transfers')}
                className="w-full py-2.5 px-3 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] font-bold text-xs rounded-xl transition-colors flex items-center justify-center gap-1.5"
              >
                <span>Compare all transfer options</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* 2. RELATED TOOLS CARD                                        */}
            {/* ------------------------------------------------------------- */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4">
              <h3 className="text-base font-bold text-[#1A1A1A] tracking-tight">
                Related Tools
              </h3>

              <div className="space-y-2">
                {[
                  {
                    id: 'exchange-rates-matrix',
                    title: 'Exchange Rates Table',
                    desc: 'View comprehensive live matrix and cross-rates',
                    icon: Building2,
                  },
                  {
                    id: 'historical-fx',
                    title: 'Historical FX',
                    desc: 'Explore multi-year historical rate trends',
                    icon: Calendar,
                  },
                  {
                    id: 'compare-transfers',
                    title: 'Transfer Comparison',
                    desc: 'Find the optimal remittance provider & speed',
                    icon: Send,
                  },
                  {
                    id: 'inflation-purchasing-power',
                    title: 'Purchasing Power',
                    desc: 'Measure inflation and domestic buying capacity',
                    icon: TrendingDown,
                  },
                ].map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <button
                      key={tool.id}
                      onClick={() => onSelectTool(tool.id)}
                      className="w-full p-3 rounded-xl border border-[#F3F4F6] hover:border-[#BFDBFE] hover:bg-[#F8FAFC] transition-all text-left flex items-start gap-3 group"
                    >
                      <div className="p-2 rounded-lg bg-[#EFF6FF] text-[#2563EB] shrink-0 group-hover:scale-105 transition-transform">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-bold text-[#1A1A1A] group-hover:text-[#2563EB] transition-colors block">
                          {tool.title}
                        </span>
                        <span className="text-[11px] text-[#6B7280] leading-snug line-clamp-1 block mt-0.5">
                          {tool.desc}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* 3. METHODOLOGY CARD                                          */}
            {/* ------------------------------------------------------------- */}
            <div className="p-5 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-2 text-xs">
              <span className="font-bold text-[#1A1A1A] block">
                Methodology & Rate Policy
              </span>
              <p className="text-[11px] text-[#6B7280] leading-relaxed">
                We use the wholesale mid-market exchange rate, which is the exact midpoint between the buy and sell quotes in global interbank currency markets. This rate is provided for informational and comparative purposes. Commercial banks and payment providers typically add an undisclosed margin of 2% to 4% above the mid-market rate.
              </p>
              <div className="pt-2 border-t border-[#F3F4F6] flex items-center justify-between text-[11px]">
                <span className="text-[#9CA3AF] font-medium">Independent & Non-Biased</span>
                <button
                  type="button"
                  onClick={() => onNavigate('governance')}
                  className="font-bold text-[#2563EB] hover:underline"
                >
                  Governance Policy →
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* =================================================================== */}
      {/* 4. MODALS: FROM & TO CURRENCY SEARCH SELECTORS                      */}
      {/* =================================================================== */}
      {fromSearchOpen && (
        <CurrencySearchModal
          title="Select Source Currency (From)"
          currencies={filteredFromCurrencies}
          selectedCode={fromCurrency}
          searchQuery={fromSearchQuery}
          onSearchChange={setFromSearchQuery}
          onSelect={(code) => {
            setFromCurrency(code);
            setFromSearchOpen(false);
          }}
          onClose={() => setFromSearchOpen(false)}
        />
      )}

      {toSearchOpen && (
        <CurrencySearchModal
          title="Select Target Currency (To)"
          currencies={filteredToCurrencies}
          selectedCode={toCurrency}
          searchQuery={toSearchQuery}
          onSearchChange={setToSearchQuery}
          onSelect={(code) => {
            setToCurrency(code);
            setToSearchOpen(false);
          }}
          onClose={() => setToSearchOpen(false)}
        />
      )}

      {/* =================================================================== */}
      {/* 5. MODAL: RATE ALERT CONFIGURATION                                  */}
      {/* =================================================================== */}
      {alertModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-[#EFF6FF] text-[#2563EB]">
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#1A1A1A]">Create Rate Alert</h3>
                  <p className="text-xs text-[#6B7280]">{fromCurrency} / {toCurrency}</p>
                </div>
              </div>
              <button
                onClick={() => setAlertModalOpen(false)}
                className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateAlert} className="space-y-4 pt-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#1A1A1A]">
                  Current Reference Rate
                </label>
                <div className="p-3 bg-[#F9FAFB] rounded-xl text-xs font-mono font-bold text-[#1A1A1A] border border-[#E5E7EB]">
                  1 {fromCurrency} = {formatRateDisplay(observation.rate)} {toCurrency}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#1A1A1A]">
                  Alert when rate crosses above
                </label>
                <input
                  type="text"
                  value={alertTargetRate}
                  onChange={(e) => setAlertTargetRate(e.target.value)}
                  placeholder={formatRateDisplay(observation.rate * 1.02)}
                  className="w-full p-3 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A] outline-none focus:border-[#2563EB]"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-[#1A1A1A]">
                  Notification Channel
                </label>
                <input
                  type="email"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  placeholder={isLoggedIn ? identityState.user.email : 'your.email@domain.com'}
                  className="w-full p-3 bg-white border border-[#E5E7EB] rounded-xl text-xs font-medium text-[#1A1A1A] outline-none focus:border-[#2563EB]"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setAlertModalOpen(false)}
                  className="flex-1 py-2.5 px-4 bg-[#F3F4F6] text-[#4B5563] font-bold text-xs rounded-xl hover:bg-[#E5E7EB] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 px-4 bg-[#2563EB] text-white font-bold text-xs rounded-xl hover:bg-[#1D4ED8] transition-colors shadow-xs"
                >
                  Activate Alert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* =================================================================== */
/* MODAL COMPONENT: CURRENCY SEARCH PICKER                             */
/* =================================================================== */

interface CurrencySearchModalProps {
  title: string;
  currencies: CurrencyMetadata[];
  selectedCode: string;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onSelect: (code: string) => void;
  onClose: () => void;
}

const CurrencySearchModal: React.FC<CurrencySearchModalProps> = ({
  title,
  currencies,
  selectedCode,
  searchQuery,
  onSearchChange,
  onSelect,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-150 overflow-hidden">
        {/* Modal Header */}
        <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#1A1A1A]">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close currency picker"
            className="p-1 text-[#9CA3AF] hover:text-[#1A1A1A] rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-[#F3F4F6] bg-[#F9FAFB]">
          <div className="relative">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search by code, name, or country (e.g. EUR, Yen, Canada)..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-semibold text-[#1A1A1A] placeholder-[#9CA3AF] outline-none focus:border-[#2563EB]"
            />
          </div>
        </div>

        {/* Currencies List */}
        <div className="flex-1 overflow-y-auto p-2 divide-y divide-[#F3F4F6]">
          {currencies.length > 0 ? (
            currencies.map((c) => {
              const isSelected = c.code === selectedCode;
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => onSelect(c.code)}
                  className={`w-full p-3 rounded-xl flex items-center justify-between text-left transition-all ${
                    isSelected
                      ? 'bg-[#EFF6FF] text-[#2563EB]'
                      : 'hover:bg-[#F9FAFB] text-[#1A1A1A]'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-2xl shrink-0">{c.flagEmoji}</span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold">{c.code}</span>
                        <span className="text-[11px] text-[#6B7280] font-normal truncate">
                          {c.name}
                        </span>
                      </div>
                      <span className="text-[10px] text-[#9CA3AF] block truncate">
                        {c.countries.slice(0, 2).join(', ')} {c.countries.length > 2 ? `+${c.countries.length - 2}` : ''}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-mono font-bold text-[#6B7280]">
                      {c.symbol}
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-[#2563EB]" />}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="p-8 text-center text-xs text-[#6B7280]">
              No currencies match your search query.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
